import React from "react";
import PropTypes from "prop-types";
import { getMessage } from "../../data/locales";
import { buildTestLaunchHref } from "../../utils/buildTestLaunchHref";
import {
  collectArchivosFromNode,
  collectTestRefsFromNode,
} from "../../utils/topicTemarioCollectors";
import { buildTopicNodeDomId } from "../../utils/studentNavPaths";
import { displayArchivoNombre } from "../../utils/archivoDisplayName";
import { listContenidoItems } from "../../utils/contenidoOrden";

function pathsEqual(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}

const temarioNodeShape = PropTypes.shape({
  nombre: PropTypes.string,
  archivos: PropTypes.arrayOf(PropTypes.object),
  testRefs: PropTypes.arrayOf(
    PropTypes.shape({
      titulo: PropTypes.string,
      bloque: PropTypes.string,
    }),
  ),
  hijos: PropTypes.arrayOf(PropTypes.object),
});

function getFileIcon(fileName = "") {
  const name = String(fileName).toLowerCase();
  if (name.endsWith(".pdf")) return "📄";
  if (name.endsWith(".mp4") || name.endsWith(".mov")) return "🎥";
  if (name.endsWith(".mp3") || name.endsWith(".wav")) return "🎧";
  return "📁";
}

function getVerContenidoButtonState({ node, cursoId, isLoading }) {
  const archivos = collectArchivosFromNode(node);
  const testRefs = collectTestRefsFromNode(node);
  const hasArtifacts = archivos.length > 0 || testRefs.length > 0;
  return {
    disabled: isLoading || (!hasArtifacts && !cursoId),
    title: resolveVerContenidoTitle(archivos.length > 0, cursoId),
  };
}

function resolveVerContenidoTitle(hasFiles, cursoId) {
  if (hasFiles) {
    return getMessage("topic.openContent");
  }
  if (cursoId) {
    return getMessage("topic.goToCourseNoFiles");
  }
  return getMessage("topic.courseNoId");
}

function TopicResourceList({
  node,
  archivos,
  testRefs,
  contenidoOrden,
  pestanaActiva,
  openResource,
  loadingArchivo,
  getArchivoLoadingKey,
  navigate,
  onTestRefOpen,
  maxArchivosPreview,
}) {
  const files = Array.isArray(archivos) ? archivos : [];
  const refs = Array.isArray(testRefs) ? testRefs : [];
  const items = listContenidoItems(
    node || { archivos: files, testRefs: refs, contenidoOrden },
  );
  const previewLimit = typeof maxArchivosPreview === "number" ? maxArchivosPreview : files.length;
  let shownFiles = 0;
  const visibleItems = items.filter((item) => {
    if (item.kind !== "archivo") {
      return true;
    }
    if (shownFiles >= previewLimit) {
      return false;
    }
    shownFiles += 1;
    return true;
  });
  const hasMoreFiles = files.length > previewLimit;

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="topic-resources-list">
      {visibleItems.map((item) => {
        if (item.kind === "test") {
          const ref = item.testRef;
          const titulo = typeof ref?.titulo === "string" ? ref.titulo.trim() : "";
          if (!titulo) {
            return null;
          }
          return (
            <button
              key={`test-ref-${item.sourceIndex}-${titulo}`}
              type="button"
              className="topic-resource-item topic-resource-link topic-resource-test"
              onClick={() => {
                if (onTestRefOpen) {
                  onTestRefOpen(ref);
                  return;
                }
                navigate(buildTestLaunchHref(ref, pestanaActiva));
              }}
            >
              <span>📝</span>
              <span className="topic-resource-label">
                {getMessage("topic.testResourceLabel", { title: titulo })}
              </span>
            </button>
          );
        }
        const archivo = item.archivo;
        return (
          <button
            key={`file-${archivo?.nombre ?? item.sourceIndex}`}
            type="button"
            className="topic-resource-item topic-resource-link"
            onClick={() => openResource(archivo, item.sourceIndex)}
            disabled={loadingArchivo === getArchivoLoadingKey(archivo)}
          >
            <span>{getFileIcon(archivo?.nombre)}</span>
            <span className="topic-resource-label">{displayArchivoNombre(archivo)}</span>
          </button>
        );
      })}
      {hasMoreFiles ? (
        <div className="topic-resource-item topic-resource-more">
          + {files.length - previewLimit} {getMessage("topic.moreResources")}
        </div>
      ) : null}
    </div>
  );
}

TopicResourceList.propTypes = {
  node: temarioNodeShape,
  contenidoOrden: PropTypes.arrayOf(PropTypes.object),
  archivos: PropTypes.arrayOf(PropTypes.object),
  testRefs: PropTypes.arrayOf(PropTypes.object),
  pestanaActiva: PropTypes.string.isRequired,
  openResource: PropTypes.func.isRequired,
  loadingArchivo: PropTypes.string,
  getArchivoLoadingKey: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  onTestRefOpen: PropTypes.func,
  maxArchivosPreview: PropTypes.number,
};

function TopicTemarioNestedNode({
  node,
  depth,
  navPathPrefix,
  focusNavPath,
  pestanaActiva,
  openResource,
  loadingArchivo,
  getArchivoLoadingKey,
  navigate,
  onTestRefOpen,
}) {
  if (!node || typeof node !== "object") {
    return null;
  }
  const hijos = Array.isArray(node.hijos) ? node.hijos : [];
  const headingTag = depth <= 1 ? "h4" : "h5";
  const Heading = headingTag;
  const domId = buildTopicNodeDomId(navPathPrefix);
  const isFocused = pathsEqual(focusNavPath, navPathPrefix);
  const openNodeResource = (archivo, archivoIndex) => {
    openResource(archivo, archivoIndex, navPathPrefix, node);
  };

  return (
    <div
      id={domId || undefined}
      className={`topic-temario-nested depth-${Math.min(depth, 3)}${isFocused ? " is-nav-focused" : ""}`}
    >
      <Heading className="topic-temario-nested-title">{node.nombre || "—"}</Heading>
      <TopicResourceList
        node={node}
        archivos={node.archivos}
        testRefs={node.testRefs}
        pestanaActiva={pestanaActiva}
        openResource={openNodeResource}
        loadingArchivo={loadingArchivo}
        getArchivoLoadingKey={getArchivoLoadingKey}
        navigate={navigate}
        onTestRefOpen={onTestRefOpen}
      />
      {hijos.length > 0 ? (
        <div className="topic-temario-nested-children">
          {hijos.map((child, idx) => (
            <TopicTemarioNestedNode
              key={`${node.nombre ?? "node"}-${idx}`}
              node={child}
              depth={depth + 1}
              navPathPrefix={[...navPathPrefix, idx]}
              focusNavPath={focusNavPath}
              pestanaActiva={pestanaActiva}
              openResource={openResource}
              loadingArchivo={loadingArchivo}
              getArchivoLoadingKey={getArchivoLoadingKey}
              navigate={navigate}
              onTestRefOpen={onTestRefOpen}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

TopicTemarioNestedNode.propTypes = {
  node: temarioNodeShape.isRequired,
  depth: PropTypes.number.isRequired,
  navPathPrefix: PropTypes.arrayOf(PropTypes.number).isRequired,
  focusNavPath: PropTypes.arrayOf(PropTypes.number),
  pestanaActiva: PropTypes.string.isRequired,
  openResource: PropTypes.func.isRequired,
  loadingArchivo: PropTypes.string,
  getArchivoLoadingKey: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  onTestRefOpen: PropTypes.func,
};

export default function TopicTemarioNodeCard({
  node,
  navPathPrefix,
  focusNavPath,
  loadingArchivo,
  getArchivoLoadingKey,
  onVerContenido,
  onNavigateNode,
  cursoId,
}) {
  if (!node || typeof node !== "object") {
    return null;
  }

  const hijos = Array.isArray(node.hijos) ? node.hijos : [];
  const hasChildren = hijos.length > 0;
  const isLoading =
    loadingArchivo !== null &&
    collectArchivosFromNode(node).some((a) => getArchivoLoadingKey(a) === loadingArchivo);
  const { disabled: verContenidoDisabled, title: verContenidoTitle } = getVerContenidoButtonState({
    node,
    cursoId,
    isLoading,
  });
  const domId = buildTopicNodeDomId(navPathPrefix);
  const isFocused = pathsEqual(focusNavPath, navPathPrefix);
  const actionTitle = hasChildren ? getMessage("topic.nextLevelTitle") : verContenidoTitle;
  let actionLabel = getMessage("topic.viewContent");
  if (isLoading) {
    actionLabel = getMessage("topic.loading");
  }

  return (
    <div className={`topic-card${isFocused ? " is-nav-focused" : ""}`} id={domId || undefined}>
      <div className="topic-card-header">
        <h3 className="topic-title">{node.nombre}</h3>
      </div>

      <div className="topic-card-footer">
        <button
          type="button"
          className="btn-access"
          disabled={hasChildren ? false : verContenidoDisabled}
          onClick={() => {
            if (hasChildren) {
              onNavigateNode(navPathPrefix);
              return;
            }
            onVerContenido(node, navPathPrefix);
          }}
          title={actionTitle}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

TopicTemarioNodeCard.propTypes = {
  node: temarioNodeShape.isRequired,
  navPathPrefix: PropTypes.arrayOf(PropTypes.number).isRequired,
  focusNavPath: PropTypes.arrayOf(PropTypes.number),
  loadingArchivo: PropTypes.string,
  getArchivoLoadingKey: PropTypes.func.isRequired,
  onVerContenido: PropTypes.func.isRequired,
  onNavigateNode: PropTypes.func.isRequired,
  cursoId: PropTypes.string,
};

export { TopicResourceList, TopicTemarioNestedNode };
